import { UseQueryResult, useQuery } from '@tanstack/react-query'
import { gql } from 'graphql-request'

import { IconNames } from 'lib/settings/icons'

import { useGraphQLClient } from '@/hooks/useGraphQLClient'
import {
  AutoCompleteApiInput,
  AutoCompleteApiResult,
  AutoCompleteRecord,
} from '@/types/autoComplete'
import { autoCompleteSessionToken } from '@/utils/autoComplete'
import { brand } from '@/utils/brand'
import { CamelToSnakeCase, camelToSnakeCase } from '@/utils/casingHelpers'
import { getLambdaEndpoint } from '@/utils/urlHelpers'

export type AutoCompleteItem = CamelToSnakeCase<AutoCompleteRecord>

const GET_AUTOCOMPLETE_QUERY = gql`
  query ($input: AutoCompleteInput!) {
    getAutoComplete(input: $input) {
      ... on AddressPoint {
        id
        type
        name
        formattedAddress
        iconUrl
      }
      ... on Club {
        id
        type
        name
        formattedAddress
        iconUrl
        questionnaireEnabled
        allowedSameDayPickup
        questionnaireEnabled
        phone
        clubId
        clubName
        accountNumber
        address {
          address1
          address2
          city
          state
          zip
          countryCode
        }
      }
      ... on GooglePlace {
        id
        type
        name
        formattedAddress
        iconUrl
        subTypes
        structuredFormatting {
          mainText
          mainTextMatchedSubstrings {
            length
            offset
          }
          secondaryText
        }
      }
    }
  }
`

export const mapIconURL = (iconURL: string = ''): IconNames => {
  switch (true) {
    case /golf/i.test(iconURL):
      return 'golf'
    case /lodge|ski/i.test(iconURL):
      return 'skiresort'
    case /cruise/i.test(iconURL):
      return 'cruise'
    case /camp/i.test(iconURL):
      return 'camps'
    case /school|university|academy|college/i.test(iconURL):
      return 'school'
    case /hotel/i.test(iconURL):
      return 'hotel'
    default:
      return 'location'
  }
}

export default function useAutoCompleteApi(
  params: AutoCompleteApiInput,
): UseQueryResult<AutoCompleteItem[], Error> {
  const client = useGraphQLClient(getLambdaEndpoint(brand.key))

  const fetchAutoComplete = async (): Promise<AutoCompleteItem[]> => {
    const data = await client.request<AutoCompleteApiResult>(GET_AUTOCOMPLETE_QUERY, {
      input: {
        brand: brand.key,
        resultType: 'all',
        sessiontoken: autoCompleteSessionToken(),
        ...params,
      },
    })

    return data.getAutoComplete.map(camelToSnakeCase<AutoCompleteRecord>)
  }

  return useQuery<AutoCompleteItem[], Error>({
    queryKey: ['autocomplete', brand.key, params.term, params.countryCode],
    queryFn: fetchAutoComplete,
    enabled: !!params.term && params.term.length > 2,
  })
}
